import { ApolloError, type FetchResult, type OperationVariables } from '@apollo/client'
import { type MutationResponse } from './useApolloMutationWrapper'

export function useMappedArrayMutationWrapper<
  TResult,
  TPayload,
  TData extends FetchResult<TPayload>,
  TVariables extends OperationVariables = OperationVariables
>({
  responses,
  variables,
  mapResponse,
  execute,
  isLoading,
  reset,
}: {
  responses: TData[] | null
  variables: TVariables | null
  execute: (variables?: TVariables) => void
  reset: () => void
  isLoading: boolean
  mapResponse: (data: TData[], variables: TVariables) => TResult
}): MutationResponse<TResult, TVariables> {
  const graphQLErrors = (responses ?? []).flatMap(result => result.errors ?? [])
  const hasErrors = graphQLErrors.length > 0

  if (hasErrors) {
    const error = new ApolloError({
      graphQLErrors,
      errorMessage: `Unable to fetch data`,
    })
    return {
      type: 'error',
      isLoading: false,
      error,
      execute,
      reset,
    }
  }
  if (isLoading) {
    return {
      type: 'pending',
      isLoading: true,
      execute,
      reset,
    }
  }
  if (responses === null || variables === null) {
    return {
      type: 'pending',
      isLoading: false,
      execute,
      reset,
    }
  }
  const parsedData = mapResponse(responses, variables)
  return {
    type: 'success',
    data: parsedData,
    isLoading: false,
    execute,
    reset,
  }
}
